/* Click-to-call button using Twilio Voice JS SDK.
 *
 * Flow:
 *  1. User clicks → we fetch a Voice Access Token from /api/voice/token.
 *  2. Lazy-init `Twilio.Device(token)` (asks for mic permission).
 *  3. `device.connect({ params: { To: '+15551234567' } })` →
 *     Twilio hits our TwiML App's Voice URL = /api/voice/twiml/outbound,
 *     which returns TwiML that dials the seller/buyer with
 *     `record="record-from-ringing-dual"`.
 *  4. In-call UI shows duration, mute, hangup.
 *  5. When the call ends, the recording arrives ~10s later via
 *     /api/voice/recording-status webhook → appears in the
 *     <RecordingsList /> on the same Lead/Buyer page.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Device } from "@twilio/voice-sdk";
import { Phone, PhoneOff, Mic, MicOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

function fmtDur(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function CallButton({ phone, label = "Call", variant = "icon", onCallEnded }) {
  const [device, setDevice] = useState(null);
  const [call, setCall] = useState(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(""); // 'ringing' | 'connected' | ''
  const [muted, setMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [eligible, setEligible] = useState(null); // null=unknown, true/false
  const timerRef = useRef(null);

  // Lazy capability check — call /api/voice/status once.
  useEffect(() => {
    let alive = true;
    api.get("/voice/status")
      .then(({ data }) => { if (alive) setEligible(!!data.voice_connected); })
      .catch(() => { if (alive) setEligible(false); });
    return () => { alive = false; };
  }, []);

  // Tear down the device when this component unmounts.
  useEffect(() => () => {
    if (call) try { call.disconnect(); } catch { /* ignore */ }
    if (device) try { device.destroy(); } catch { /* ignore */ }
    if (timerRef.current) clearInterval(timerRef.current);
  }, [device, call]);

  const ensureDevice = useCallback(async () => {
    if (device) return device;
    const { data } = await api.get("/voice/token");
    const d = new Device(data.token, {
      logLevel: "warn",
      codecPreferences: ["opus", "pcmu"],
    });
    d.on("error", (err) => {
      console.error("[voice] device error", err);
      toast.error(`Call error: ${err?.message || "unknown"}`);
    });
    await d.register();
    setDevice(d);
    return d;
  }, [device]);

  const startCall = async () => {
    if (!phone) {
      toast.error("No phone number on file for this contact.");
      return;
    }
    setBusy(true);
    let acknowledgeConsent = false;
    try {
      // TCPA / DNC pre-flight. If the destination is on the National DNC
      // registry per our skip-trace cache, force the user to attest to
      // prior express written consent before we let the call go out.
      try {
        const { data: dnc } = await api.get(`/twilio/dnc-check`, { params: { to: phone } });
        if (dnc?.is_dnc) {
          const ok = window.confirm(
            `WARNING — TCPA risk.\n\nThis number is on the National Do-Not-Call registry. ` +
            `Calling without prior express written consent could trigger a $500–$1,500 ` +
            `penalty per violation.\n\nClick OK only if you have written consent on file ` +
            `for this contact. We will log your attestation for compliance audit.`,
          );
          if (!ok) {
            toast.error("Call blocked — number is on the DNC registry.");
            setBusy(false);
            return;
          }
          acknowledgeConsent = true;
        }
      } catch (e) {
        // DNC check failure shouldn't hard-block the call (e.g. free tier
        // 402/403). Log it and continue — the backend TwiML guard will
        // still enforce.
        console.warn("[voice] dnc pre-check failed", e?.response?.status);
      }

      const d = await ensureDevice();
      const c = await d.connect({
        params: acknowledgeConsent
          ? { To: phone, AcknowledgeConsent: "true" }
          : { To: phone },
      });
      setCall(c);
      setStatus("ringing");
      setDuration(0);

      c.on("accept", () => {
        setStatus("connected");
        timerRef.current = setInterval(() => setDuration((s) => s + 1), 1000);
      });
      c.on("disconnect", () => {
        setStatus("");
        setCall(null);
        setMuted(false);
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        if (onCallEnded) onCallEnded();
        toast.success("Call ended — recording will appear shortly.");
      });
      c.on("cancel", () => { setStatus(""); setCall(null); });
      c.on("reject", () => {
        setStatus("");
        setCall(null);
        toast.error("Call rejected");
      });
    } catch (e) {
      console.error("[voice] start call failed", e);
      toast.error(e?.response?.data?.detail || e?.message || "Could not start call");
      setStatus("");
    } finally {
      setBusy(false);
    }
  };

  const hangup = () => {
    if (call) try { call.disconnect(); } catch { /* ignore */ }
  };

  const toggleMute = () => {
    if (!call) return;
    const next = !muted;
    try {
      call.mute(next);
      setMuted(next);
    } catch (e) { console.error("[voice] mute failed", e); }
  };

  // ── Active call UI ───────────────────────────────────────────────────
  if (call) {
    return (
      <div
        data-testid="call-active-bar"
        className="fixed bottom-4 right-4 z-50 bg-emerald-600 text-white rounded-xl shadow-2xl px-4 py-3 flex items-center gap-3 min-w-[280px]"
      >
        <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center">
          <Phone size={18} className={status === "connected" ? "" : "animate-pulse"} />
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wider opacity-80">
            {status === "connected" ? "On call" : "Ringing…"}
          </div>
          <div className="font-mono font-bold text-base">
            {status === "connected" ? fmtDur(duration) : phone}
          </div>
        </div>
        <button
          type="button"
          onClick={toggleMute}
          disabled={status !== "connected"}
          className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition disabled:opacity-50"
          title={muted ? "Unmute" : "Mute"}
          data-testid="call-mute-btn"
        >
          {muted ? <MicOff size={16} /> : <Mic size={16} />}
        </button>
        <button
          type="button"
          onClick={hangup}
          className="w-9 h-9 rounded-full bg-red-500 hover:bg-red-400 flex items-center justify-center transition"
          title="Hang up"
          data-testid="call-hangup-btn"
        >
          <PhoneOff size={16} />
        </button>
      </div>
    );
  }

  // ── Idle button (icon vs full variants) ──────────────────────────────
  const disabled = busy || eligible === false || !phone;
  const tooltip = eligible === false
    ? "Connect Twilio Voice in Settings → Twilio to enable click-to-call."
    : !phone ? "No phone number" : "Call & record";

  if (variant === "icon") {
    return (
      <button
        type="button"
        onClick={startCall}
        disabled={disabled}
        title={tooltip}
        data-testid="call-btn"
        className="inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-emerald-50 text-emerald-700 disabled:text-slate-300 disabled:hover:bg-transparent disabled:cursor-not-allowed transition"
      >
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Phone size={13} />}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={startCall}
      disabled={disabled}
      title={tooltip}
      data-testid="call-btn"
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold transition disabled:bg-emerald-300 disabled:cursor-not-allowed"
    >
      {busy ? <Loader2 size={14} className="animate-spin" /> : <Phone size={14} />}
      {label}
    </button>
  );
}
